// src/components/ProductTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

const ProductTable = ({ items, onEdit, onDelete, onToggleVisibility }) => {
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Очікуємо дерево з /api/categories
        const { data } = await api.get("/categories");
        if (!mounted) return;
        setCategories(extractFlat(data));
      } catch (e) {
        if (!mounted) return;
        console.error("Не вдалось отримати категорії:", e);
        setCategories([]);
      } finally {
        if (mounted) setLoadingCats(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Плоский масив -> мапа для швидкої побудови шляху
  const { byId } = useMemo(() => {
    const map = new Map();
    for (const c of categories) map.set(c.id, c);
    return { byId: map };
  }, [categories]);

  // джерело фото (пріоритет: Cloudinary URL з бекенду -> будь-який http(s) -> локальний uploads)
  const getMainImageSrc = (item) => {
    if (item?.mainImageUrl) return item.mainImageUrl; // з бекенду (Cloudinary https-URL)
    const img = item?.mainImage;
    if (!img) return null;
    if (typeof img === "string" && img.startsWith("http")) return img; // раптом уже повний URL
    return `${API_BASE}/uploads/${img}`; // старий локальний варіант
  };

  // Якщо в item.Category є вкладені parent-и — читаємо звідти (пріоритет)
  const getPathFromNested = (cat) => {
    if (!cat) return "";
    const names = [];
    let cur = cat;
    const guard = new Set();
    while (cur && !guard.has(cur.id)) {
      guard.add(cur.id);
      if (cur.name) names.unshift(cur.name);
      cur = cur.parent || cur.Parent || cur.parentCategory || null;
    }
    return names.join(" → ");
  };

  // Якщо вкладених нема — відновлюємо шлях із мапи byId через parentId
  const getPathFromMaps = (catId) => {
    if (!catId || !byId.size) return "";
    const names = [];
    let cur = byId.get(catId);
    const guard = new Set();
    while (cur && !guard.has(cur.id)) {
      guard.add(cur.id);
      if (cur.name) names.unshift(cur.name);
      cur = cur.parentId ? byId.get(cur.parentId) : null;
    }
    return names.join(" → ");
  };

  // Універсальна функція побудови шляху
  const getCategoryPath = (item) => {
    if (item?.Category && (item.Category.parent || item.Category.Parent)) {
      const nested = getPathFromNested(item.Category);
      if (nested) return nested;
    }
    const catId = item?.categoryId ?? item?.Category?.id ?? item?.category_id ?? null;
    const fromMap = getPathFromMaps(catId);
    return fromMap || (item?.Category?.name ?? "-");
  };

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow-md border border-[#D2B48C]">
      <table className="w-full border-collapse">
        <thead className="bg-[#4B2E2B] text-white">
          <tr>
            <th className="p-3 text-left">Фото</th>
            <th className="p-3 text-left">Назва</th>
            <th className="p-3 text-left">Ціна</th>
            <th className="p-3 text-left">Категорія / Підкатегорія</th>
            <th className="p-3 text-left">Статус</th>
            <th className="p-3 text-left w-[220px]">Дії</th>
          </tr>
        </thead>

        <tbody>
          {items.length === 0 && (
            <tr>
              <td colSpan="6" className="text-center p-4 text-gray-500">
                Немає продуктів
              </td>
            </tr>
          )}

          {items.map((item) => {
            const categoryPath = getCategoryPath(item) || "-";
            const imgSrc = getMainImageSrc(item);
            const hidden = !!item.isHidden;

            return (
              <tr
                key={item.id}
                className={`border-b hover:bg-[#F5F0E6] ${hidden ? "opacity-60" : ""}`}
                title={hidden ? "Цей товар прихований на сайті" : undefined}
              >
                <td className="p-3">
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={item.name}
                      className="h-16 w-16 object-cover rounded border"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-16 w-16 bg-gray-100 flex items-center justify-center text-gray-400 rounded">
                      No
                    </div>
                  )}
                </td>

                <td className="p-3 font-semibold text-[#4B2E2B]">
                  {item.name}
                  {hidden && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs border border-gray-300 bg-gray-50 text-gray-600">
                      приховано
                    </span>
                  )}
                </td>

                <td className="p-3">
                  {typeof item.price === "number" ? `${item.price} ₴` : "—"}
                </td>

                <td className="p-3">
                  {loadingCats ? (
                    <span className="text-gray-400">…завантаження категорій</span>
                  ) : (
                    categoryPath
                  )}
                </td>

                <td className="p-3">
                  {hidden ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs border border-gray-300 bg-gray-50 text-gray-700">
                      Hidden
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs border border-green-200 bg-green-50 text-green-700">
                      Active
                    </span>
                  )}
                </td>

                <td className="p-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => onEdit(item)}
                      className="bg-[#A47551] text-white px-3 py-1 rounded hover:bg-[#8B5E3C]"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => onDelete(item.id)}
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                    >
                      Delete
                    </button>

                    {typeof onToggleVisibility === "function" && (
                      <button
                        onClick={() => onToggleVisibility(item)}
                        className={
                          hidden
                            ? "bg-emerald-600 text-white px-3 py-1 rounded hover:bg-emerald-700"
                            : "bg-gray-700 text-white px-3 py-1 rounded hover:bg-gray-800"
                        }
                        title={hidden ? "Зробити товар видимим на сайті" : "Приховати товар на сайті"}
                      >
                        {hidden ? "Show" : "Hide"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/** Допоміжне: витягнути ПЛОСКИЙ список категорій з будь-якої структури (дерево/обгортка) */
function extractFlat(input) {
  if (Array.isArray(input)) {
    const flat = [];
    const stack = [...input];
    while (stack.length) {
      const node = stack.pop();
      if (!node || typeof node !== "object") continue;
      const { subcategories, ...rest } = node;
      flat.push(rest);
      if (Array.isArray(subcategories)) {
        for (const child of subcategories) stack.push(child);
      }
    }
    return flat;
  }
  if (Array.isArray(input?.items)) return extractFlat(input.items);
  if (Array.isArray(input?.data)) return extractFlat(input.data);
  if (Array.isArray(input?.categories)) return extractFlat(input.categories);
  return [];
}

export default ProductTable;



// // src/components/ProductTable.jsx
// import React, { useEffect, useMemo, useState } from "react";
// import api from "../../api";

// const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

// const ProductTable = ({ items, onEdit, onDelete }) => {
//   const [categories, setCategories] = useState([]);
//   const [loadingCats, setLoadingCats] = useState(true);

//   useEffect(() => {
//     let mounted = true;
//     (async () => {
//       try {
//         // Очікуємо дерево з /api/categories
//         const { data } = await api.get("/categories");
//         if (!mounted) return;
//         setCategories(extractFlat(data));
//       } catch (e) {
//         if (!mounted) return;
//         console.error("Не вдалось отримати категорії:", e);
//         setCategories([]);
//       } finally {
//         if (mounted) setLoadingCats(false);
//       }
//     })();
//     return () => {
//       mounted = false;
//     };
//   }, []);

//   // Плоский масив -> мапа для швидкої побудови шляху
//   const { byId } = useMemo(() => {
//     const map = new Map();
//     for (const c of categories) map.set(c.id, c);
//     return { byId: map };
//   }, [categories]);

//   // джерело фото (пріоритет: Cloudinary URL з бекенду -> будь-який http(s) -> локальний uploads)
//   const getMainImageSrc = (item) => {
//     if (item?.mainImageUrl) return item.mainImageUrl; // з бекенду (Cloudinary https-URL)
//     const img = item?.mainImage;
//     if (!img) return null;
//     if (typeof img === "string" && img.startsWith("http")) return img; // раптом уже повний URL
//     return `${API_BASE}/uploads/${img}`; // старий локальний варіант
//   };

//   // Якщо в item.Category є вкладені parent-и — читаємо звідти (пріоритет)
//   const getPathFromNested = (cat) => {
//     if (!cat) return "";
//     const names = [];
//     let cur = cat;
//     const guard = new Set();
//     while (cur && !guard.has(cur.id)) {
//       guard.add(cur.id);
//       if (cur.name) names.unshift(cur.name);
//       cur = cur.parent || cur.Parent || cur.parentCategory || null;
//     }
//     return names.join(" → ");
//   };

//   // Якщо вкладених нема — відновлюємо шлях із мапи byId через parentId
//   const getPathFromMaps = (catId) => {
//     if (!catId || !byId.size) return "";
//     const names = [];
//     let cur = byId.get(catId);
//     const guard = new Set();
//     while (cur && !guard.has(cur.id)) {
//       guard.add(cur.id);
//       if (cur.name) names.unshift(cur.name);
//       cur = cur.parentId ? byId.get(cur.parentId) : null;
//     }
//     return names.join(" → ");
//   };

//   // Універсальна функція побудови шляху
//   const getCategoryPath = (item) => {
//     if (item?.Category && (item.Category.parent || item.Category.Parent)) {
//       const nested = getPathFromNested(item.Category);
//       if (nested) return nested;
//     }
//     const catId =
//       item?.categoryId ??
//       item?.Category?.id ??
//       item?.category_id ??
//       null;
//     const fromMap = getPathFromMaps(catId);
//     return fromMap || (item?.Category?.name ?? "-");
//   };

//   return (
//     <div className="overflow-x-auto bg-white rounded-lg shadow-md border border-[#D2B48C]">
//       <table className="w-full border-collapse">
//         <thead className="bg-[#4B2E2B] text-white">
//           <tr>
//             <th className="p-3 text-left">Фото</th>
//             <th className="p-3 text-left">Назва</th>
//             <th className="p-3 text-left">Ціна</th>
//             <th className="p-3 text-left">Категорія / Підкатегорія</th>
//             <th className="p-3 text-left w-[150px]">Дії</th>
//           </tr>
//         </thead>
//         <tbody>
//           {items.length === 0 && (
//             <tr>
//               <td colSpan="5" className="text-center p-4 text-gray-500">
//                 Немає продуктів
//               </td>
//             </tr>
//           )}

//           {items.map((item) => {
//             const categoryPath = getCategoryPath(item) || "-";
//             const imgSrc = getMainImageSrc(item);

//             return (
//               <tr key={item.id} className="border-b hover:bg-[#F5F0E6]">
//                 <td className="p-3">
//                   {imgSrc ? (
//                     <img
//                       src={imgSrc}
//                       alt={item.name}
//                       className="h-16 w-16 object-cover rounded border"
//                       loading="lazy"
//                     />
//                   ) : (
//                     <div className="h-16 w-16 bg-gray-100 flex items-center justify-center text-gray-400 rounded">
//                       No
//                     </div>
//                   )}
//                 </td>
//                 <td className="p-3 font-semibold text-[#4B2E2B]">{item.name}</td>
//                 <td className="p-3">{typeof item.price === "number" ? `${item.price} ₴` : "—"}</td>
//                 <td className="p-3">
//                   {loadingCats ? (
//                     <span className="text-gray-400">…завантаження категорій</span>
//                   ) : (
//                     categoryPath
//                   )}
//                 </td>
//                 <td className="p-3 flex gap-2">
//                   <button
//                     onClick={() => onEdit(item)}
//                     className="bg-[#A47551] text-white px-3 py-1 rounded hover:bg-[#8B5E3C]"
//                   >
//                     Edit
//                   </button>
//                   <button
//                     onClick={() => onDelete(item.id)}
//                     className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
//                   >
//                     Delete
//                   </button>
//                 </td>
//               </tr>
//             );
//           })}
//         </tbody>
//       </table>
//     </div>
//   );
// };

// /** Допоміжне: витягнути ПЛОСКИЙ список категорій з будь-якої структури (дерево/обгортка) */
// function extractFlat(input) {
//   if (Array.isArray(input)) {
//     const flat = [];
//     const stack = [...input];
//     while (stack.length) {
//       const node = stack.pop();
//       if (!node || typeof node !== "object") continue;
//       const { subcategories, ...rest } = node;
//       flat.push(rest);
//       if (Array.isArray(subcategories)) {
//         for (const child of subcategories) stack.push(child);
//       }
//     }
//     return flat;
//   }
//   if (Array.isArray(input?.items)) return extractFlat(input.items);
//   if (Array.isArray(input?.data)) return extractFlat(input.data);
//   if (Array.isArray(input?.categories)) return extractFlat(input.categories);
//   return [];
// }

// export default ProductTable;




